/**
 * Who is actually behind each M365 account.
 *
 * IG OPC signs in with ROLE mailboxes, not personal ones — `operations@`,
 * `qc1@`, `cutting3@`. MSAL reports no usable name for them, so the presence
 * bar fell back to the account itself and showed initials of the EMAIL:
 * `customersales1@` read as "CU", `operations@` as "OP", and two different
 * people both came out "CU". This maps the account to the human sitting at it,
 * so `operations@` renders "PO" for Paolo Obrero.
 *
 * Source: the tenant's own user export (Entra → Users → Export, 2026-09-07),
 * using each account's First name + Last name. Re-export and regenerate when
 * people join or leave; an account missing here is not an error.
 *
 * Two accounts from that export are deliberately NOT listed: `inventory@`
 * (whose holder has left) and `intalmiks11@` (retired, superseded by `mikko@`).
 * They are still in the tenant, so a plain re-export would put them back —
 * leave them out unless someone is actually signing in with them again.
 *
 * Display-side only. Nothing here is written to SharePoint and no access
 * decision depends on it — presence is ambient. HRIS access is governed
 * separately in `hrisAccess.ts`, and the Historical Data import roster in
 * `historicalImportAllowlist.ts`; keep all three as they are, since they
 * answer different questions and their lists deliberately differ.
 *
 * Earl Manales holds two live accounts, and both map to him on purpose — the
 * heads then read the same initials, and the hover title names the account to
 * tell them apart.
 *
 * An account with no entry falls back to whatever MSAL reports, so a new hire
 * who has not been added yet degrades to today's behaviour, never a blank head.
 */

/** Lowercase account email → the person's full name. */
export const PEOPLE_BY_EMAIL: Record<string, string> = {
  "accountsadmin@intalgarmentsopc.onmicrosoft.com": "Casey Nojara",
  "assembly2@intalgarmentsopc.onmicrosoft.com": "Steve Christian Vequiso",
  "consultant@intalgarmentsopc.onmicrosoft.com": "Jo Ubana",
  "contractorslead@intalgarmentsopc.onmicrosoft.com": "Jocelyn Guban",
  "crmsalesandassociate2@intalgarmentsopc.onmicrosoft.com": "Felix Earl Martinez",
  "crmsalesandassociate3@intalgarmentsopc.onmicrosoft.com": "John Richman Arribe",
  "crmsalesandassociate4@intalgarmentsopc.onmicrosoft.com": "Paulyn Joy Ursal",
  "crmsalesandassociate5@intalgarmentsopc.onmicrosoft.com": "Sean Ashley Samson",
  "crmsup@intalgarmentsopc.onmicrosoft.com": "Angel Lyn Teodoro",
  "customersales1@intalgarmentsopc.onmicrosoft.com": "Reyna Mae Isidro",
  "customersales@intalgarmentsopc.onmicrosoft.com": "Shyne Malaluan",
  "cutting1@intalgarmentsopc.onmicrosoft.com": "Elbert Azores",
  "cutting2@intalgarmentsopc.onmicrosoft.com": "Paul Boaquin",
  "cutting3@intalgarmentsopc.onmicrosoft.com": "Jun Corsaba",
  "cutting4@intalgarmentsopc.onmicrosoft.com": "Romeo Montion",
  "cutting5@intalgarmentsopc.onmicrosoft.com": "Ariel Perpetua",
  "cutting@intalgarmentsopc.onmicrosoft.com": "Miguel Milan",
  "cuttingsection@intalgarmentsopc.onmicrosoft.com": "Antipaz Songuad",
  "deliveryassembly@intalgarmentsopc.onmicrosoft.com": "Ryan Anguluan",
  "embroidery@intalgarmentsopc.onmicrosoft.com": "Shiela Cruz",
  "facility@intalgarmentsopc.onmicrosoft.com": "Eber Elardo",
  "finance1@intalgarmentsopc.onmicrosoft.com": "Lailani Zubiaga",
  "finance3@intalgarmentsopc.onmicrosoft.com": "Maricris Lavapiez",
  "financeandsupport@intalgarmentsopc.onmicrosoft.com": "Edelyn Pulvinar",
  "fulfillmentandinventory@intalgarmentsopc.onmicrosoft.com": "Earl Manales",
  "fulfillmentandinventoryandpackaging@intalgarmentsopc.onmicrosoft.com": "Earl Manales",
  "fulfillmentandlogistics@intalgarmentsopc.onmicrosoft.com": "Froilan Villarais",
  "fulfillmentandpackaging2@intalgarmentsopc.onmicrosoft.com": "Kyle Vequiso",
  "hrandpurchasing@intalgarmentsopc.onmicrosoft.com": "Evelyn Canaria",
  "hrmanager@intalgarmentsopc.onmicrosoft.com": "Marge Tabora",
  "inhousesewer09@intalgarmentsopc.onmicrosoft.com": "Merlinda Tulba",
  "inhousesewer1@intalgarmentsopc.onmicrosoft.com": "Ginalyn Alcantara",
  "inhousesewer2@intalgarmentsopc.onmicrosoft.com": "Teodora Brosas",
  "inhousesewer3@intalgarmentsopc.onmicrosoft.com": "Badith Cabigayan",
  "inhousesewer4@intalgarmentsopc.onmicrosoft.com": "Diana Orada",
  "inhousesewer5@intalgarmentsopc.onmicrosoft.com": "Rose Rebay",
  "inhousesewer6@intalgarmentsopc.onmicrosoft.com": "Liza Larga",
  "jpgalvez@intalgarmentsopc.onmicrosoft.com": "Jae Galvez",
  "managingowner@intalgarmentsopc.onmicrosoft.com": "Marvin Intal",
  "mikko@intalgarmentsopc.onmicrosoft.com": "Mikko Intal",
  "operations02@intalgarmentsopc.onmicrosoft.com": "Mark Anthony Libanan",
  "operations@intalgarmentsopc.onmicrosoft.com": "Paolo Obrero",
  "packaginga@intalgarmentsopc.onmicrosoft.com": "Karl Astorga",
  "prepassembly@intalgarmentsopc.onmicrosoft.com": "Marivic Bisuela",
  "projectbase@intalgarmentsopc.onmicrosoft.com": "Yves Lorque",
  "qc1@intalgarmentsopc.onmicrosoft.com": "Marivel Isidro",
  "qc2@intalgarmentsopc.onmicrosoft.com": "Rosario Luares",
  "qc3@intalgarmentsopc.onmicrosoft.com": "Elizabeth Orobia",
  "qc@intalgarmentsopc.onmicrosoft.com": "Sally Escobar",
  "salesmarketing1@intalgarmentsopc.onmicrosoft.com": "Hanz Sta Ana",
};

/**
 * The person's name for an account, or `fallback` when we don't know them.
 * Case- and whitespace-insensitive: MSAL and SharePoint disagree on casing.
 */
export function displayNameFor(email: string | null | undefined, fallback = ""): string {
  const key = (email ?? "").trim().toLowerCase();
  return PEOPLE_BY_EMAIL[key] || fallback;
}
